import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useStaffNotifications } from "../../hooks/useStaffNotifications";

/**
 * Starts the browser-notification polling, and — only when the browser has not
 * yet been asked — offers the button that requests permission.
 *
 * Mounted in the dashboard chrome rather than on a page, because the value is
 * being told about a booking while looking at something else entirely.
 *
 * The prompt is a click, never automatic. An unsolicited permission dialog gets
 * denied on reflex, and a denial is effectively permanent — almost nobody goes
 * into site settings to undo one. Asking once, in context, with the reason
 * visible, is the only shot there is.
 */
const StaffNotificationsPrompt = () => {
  const { t } = useTranslation();
  const { canAsk, request } = useStaffNotifications();
  const [dismissed, setDismissed] = useState(false);

  if (!canAsk || dismissed) return null;

  return (
    <div
      className="mb-5 flex flex-col gap-3 rounded-card border border-brand-200 bg-brand-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      role="region"
      aria-label={t("Dashboard.BrowserNotifications.title")}
    >
      <div className="flex items-start gap-2.5">
        <i
          className="bi bi-bell mt-0.5 text-brand-700"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="m-0 text-fluid-sm font-semibold text-ink-900">
            {t("Dashboard.BrowserNotifications.title")}
          </p>
          <p className="m-0 mt-0.5 text-xs text-ink-600">
            {t("Dashboard.BrowserNotifications.description")}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={request}
          className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-brand-600 bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          {t("Dashboard.BrowserNotifications.enable")}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-transparent px-3 py-2 text-sm font-semibold text-ink-500 transition hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          {t("Dashboard.BrowserNotifications.notNow")}
        </button>
      </div>
    </div>
  );
};

export default StaffNotificationsPrompt;
